import React, { useEffect, useState } from 'react'
import { Checkbox, Menu, MenuItem, Card, Position, Tag } from '@blueprintjs/core'
import { Tooltip2 } from '@blueprintjs/popover2'
import { Button, IconButton, Flex, Icon, Popover } from '@ui'
import { IBuildingDetails, IFloorDetails, IPortfolioCity } from '@api'
import { Mode, ModeMeta, LocationType } from './types'
import LocationSearch from './Search'
import { CityColumn } from './helperComponents'
import ClearableSelectWrapper from '../ClearableSelectWrapper'
import useLocations from './useLocations'
import useSelection from './useSelection'
import { IUIButtonProps } from '@ui/buttons/Button'
// import moment from 'moment'
import { DateUtil } from '@utils'
import css from './locationSelect.module.scss'
export { Mode, LocationType }

interface LocationSelectProps {
  mode: Mode
  value: string | null
  clearable?: boolean
  btnText?: string
  buttonProps?: IUIButtonProps
  target?: React.ReactNode
  popoverDisabled?: boolean
  onChange: (value: any) => void
  includeOldBuildings?: boolean
  className?: string
  checkValues?: string[]
}

export default function LocationSelect({
  mode,
  value: _value,
  checkValues,
  clearable,
  btnText,
  target,
  onChange,
  buttonProps = {},
  popoverDisabled,
  includeOldBuildings,
  ...rest
}: LocationSelectProps) {
  const regions = useLocations(includeOldBuildings)
  const value = useSelection(mode, _value, regions)
  const [cityNav, setCityNav] = useState<IPortfolioCity | null>(null)
  let selectedValue: any = 'Select'

  useEffect(() => {
    if (!_value || !regions) return
    const city = regions
      .reduce((acc: IPortfolioCity[], r) => [...acc, ...r.cities], [])
      .find((c) =>
        c.buildings.find((b) => b.id === _value || b?.floors?.find((f) => f.id === _value))
      )
    if (city) setCityNav(city)
  }, [regions, _value])

  if (_value && !value) selectedValue = 'Select'

  if (value) {
    switch (mode) {
      case Mode.SINGLE_BUILDING:
        selectedValue = <BuildingButton building={value} />
        break

      case Mode.SINGLE_FLOOR:
        selectedValue = <FloorButton floor={value} />
        break

      case Mode.SINGLE_BUILDING_FLOOR:
        if ('building' in value) selectedValue = <FloorButton floor={value} />
        else selectedValue = <BuildingButton building={value} />
        break

      default:
        selectedValue = <span>...</span>
    }
  }

  return (
    <ClearableSelectWrapper {...rest}>
      <Popover
        minimal
        disabled={popoverDisabled}
        position={Position.BOTTOM}
        modifiers={{ arrow: { enabled: false } }}
        content={
          <div className={css.container}>
            <LocationSearch
              mode={mode}
              regions={regions}
              selectCity={setCityNav}
              selectBuilding={(building) => onChange(building)}
              selectFloor={(floor) => onChange(floor)}
            />
            <Flex>
              <CityColumn regions={regions} cityNav={cityNav} setCityNav={setCityNav} />
              <BuildingColumn
                mode={mode}
                value={value}
                city={cityNav}
                onSelect={onChange}
                checkValues={checkValues}
              />
            </Flex>
          </div>
        }
      >
        {target || (
          <Button
            {...buttonProps}
            style={{ ...buttonProps.style, padding: '0 10px' }}
            rightIconName={value || btnText ? undefined : 'chevron-down'}
            text={btnText || selectedValue}
          />
        )}
      </Popover>
      {value && clearable && (
        <IconButton
          minimal
          iconName="times"
          tooltip="Clear"
          data-button="clear-button"
          onClick={() => onChange(null)}
        />
      )}
    </ClearableSelectWrapper>
  )
}

LocationSelect.Mode = Mode
LocationSelect.LocationType = LocationType

function BuildingButton({ building }: { building: IBuildingDetails }) {
  return (
    <div>
      <div className={css.normal}>{building.name}</div>
      {building.city && building.state && (
        <div className={css.small}>{`${building.city}, ${building.state}`}</div>
      )}
    </div>
  )
}
function FloorButton({ floor }: { floor: IFloorDetails }) {
  return (
    <div>
      <div
        className={css.normal}
        style={{
          maxWidth: 200,
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
        }}
      >{`${floor.name}, ${floor.building.name}`}</div>
      <div className={css.small}>{`${floor.building.city || ''}${
        floor.building.state ? `, ${floor.building.state}` : ''
      }`}</div>
    </div>
  )
}

interface BuildingColumnProps {
  mode: Mode
  value: any
  city: IPortfolioCity | null
  onSelect: (value: any, type: LocationType) => void
  checkValues?: string[]
}

function BuildingColumn({ mode, city, value, onSelect, checkValues }: BuildingColumnProps) {
  return (
    <div className={css.col} style={{ minWidth: 300 }}>
      <div className={css.col_header}>Buildings / Floors</div>
      {city ? (
        city.buildings.length ? (
          <div className={css.list}>
            <Menu>
              {city.buildings.map((b) => (
                <BuildingSection
                  key={b.id}
                  mode={mode}
                  building={b}
                  value={value}
                  onSelect={onSelect}
                  checkValues={checkValues}
                />
              ))}
            </Menu>
          </div>
        ) : (
          <Menu>
            <MenuItem disabled text="No buildings" />
          </Menu>
        )
      ) : (
        <Menu>
          <MenuItem disabled text="Select a city to see buildings" />
        </Menu>
      )}
    </div>
  )
}

function BuildingContainer({ children, mode }: { children: React.ReactNode; mode: Mode }) {
  if (!ModeMeta[mode].floor) return <div style={{ borderBottom: '1px solid #eee' }}>{children}</div>
  return <Card style={{ padding: 10, marginBottom: 10, overflow: 'hidden' }}>{children}</Card>
}

function BuildingSection({
  mode,
  value,
  building,
  onSelect,
  checkValues,
}: {
  mode: Mode
  value: any
  building: IBuildingDetails
  onSelect: (value: any, type: LocationType) => void
  checkValues?: string[]
}) {
  const modeMeta = ModeMeta[mode]
  const buildingSelect = () => {
    if (!modeMeta.building) return
    onSelect(building, LocationType.BUILDING)
  }

  return (
    <BuildingContainer mode={mode}>
      <BuildingItem {...{ building, mode, value, onClick: buildingSelect }} />
      {modeMeta.floor && <Floors {...{ building, mode, value, onSelect, checkValues }} />}
    </BuildingContainer>
  )
}

function BuildingItem({
  building,
  mode,
  onClick,
  value,
}: {
  building: IBuildingDetails
  mode: Mode
  value: any
  onClick: () => void
}) {
  const modeMeta = ModeMeta[mode]
  const active =
    value === building || (value instanceof Array && value.find((b) => b.id === building.id))
  const shouldDismissPopover = !modeMeta.multi && modeMeta.building
  let old: boolean | undefined
  if (building.closedAt) {
    old = DateUtil().isAfter(building.closedAt)
  }

  const content = (
    <Flex>
      {modeMeta.multi && <Checkbox />}
      <div>
        <div className={css.normal} style={{ fontWeight: 'bolder' }}>
          {building.name}{' '}
          {old && (
            <Tooltip2 content="Expired Building">
              <Tag minimal round style={{ fontWeight: 'normal' }}>
                Exp
              </Tag>
            </Tooltip2>
          )}
        </div>
        {building.address && (
          <div className={css.small} style={{ fontSize: 12 }}>
            {building.address}
          </div>
        )}
      </div>
    </Flex>
  )

  if (!modeMeta.building && !modeMeta.multi) return content
  return (
    <MenuItem
      active={active}
      onClick={onClick}
      shouldDismissPopover={shouldDismissPopover}
      text={content}
      data-item={building.name}
    />
  )
}

function Floors({
  building,
  mode,
  value,
  onSelect,
  checkValues,
}: {
  building: IBuildingDetails
  mode: Mode
  value: any
  onSelect: (value: any, type: LocationType) => void
  checkValues?: string[]
}) {
  const modeMeta = ModeMeta[mode]
  if (!building.floors || !building.floors.length) return <MenuItem disabled text="No floors" />

  return (
    <Menu style={{ margin: '0 -5px' }}>
      {building.floors.map((floor) => (
        <MenuItem
          disabled={checkValues && checkValues.includes(floor.id)}
          key={floor.id}
          active={value === floor}
          onClick={() => onSelect(floor, LocationType.FLOOR)}
          shouldDismissPopover={!modeMeta.multi}
          data-item={floor.name}
          text={
            <Flex justify="space-between">
              <Flex align="center">
                {modeMeta.multi && <Checkbox style={{ marginLeft: 26 }} />}
                <div>{floor.name}</div>
              </Flex>
              {floor.hidden && (
                <Tooltip2 content="This floor is hidden" position={Position.TOP}>
                  <div>
                    <Icon name="eye-slash" style={{ color: 'orange', marginLeft: 8 }} />
                  </div>
                </Tooltip2>
              )}
              {checkValues && checkValues.includes(floor.id) && <Icon name="check" />}
            </Flex>
          }
        />
      ))}
    </Menu>
  )
}
